import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { fetchLoginAudit, fetchLoginSessions } from '../../services/admin';

const toCSV = (rows) => {
  if (!rows || rows.length === 0) return '';
  const headers = ['id', 'email', 'event', 'success', 'ip', 'user_agent', 'created_at', 'social'];
  const escape = (val) => {
    if (val === null || val === undefined) return '';
    let s = String(val);
    // Neutraliser l'injection de formule (CSV injection) : préfixer d'une apostrophe
    if (s.length && ['=', '+', '-', '@', '\t', '\r'].includes(s[0])) s = "'" + s;
    s = s.replace(/"/g, '""');
    return `"${s}"`;
  };
  const lines = [headers.join(',')];
  for (const r of rows) {
    lines.push([escape(r.id), escape(r.email), escape(r.event), escape(r.success), escape(r.ip), escape(r.user_agent), escape(r.created_at), escape(r.social)].join(','));
  }
  return lines.join('\n');
};

const download = (filename, content) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.style.display = 'none';
  document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 0);
};

// Formatte une durée en secondes -> "1h 23m", "12m 05s", "42s"
const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return '—';
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, '0')}s`;
  return `${sec}s`;
};

// Transforme un User-Agent brut en libellé lisible : "Chrome sur Windows"
const parseUserAgent = (ua) => {
  if (!ua) return '—';
  let browser = 'Navigateur inconnu';
  if (/Edg\//i.test(ua)) browser = 'Edge';
  else if (/OPR\/|Opera/i.test(ua)) browser = 'Opera';
  else if (/Chrome\//i.test(ua) && !/Chromium/i.test(ua)) browser = 'Chrome';
  else if (/Firefox\//i.test(ua)) browser = 'Firefox';
  else if (/Safari\//i.test(ua) && /Version\//i.test(ua)) browser = 'Safari';
  else if (/curl/i.test(ua)) browser = 'curl (script)';
  else if (/python-requests/i.test(ua)) browser = 'Python (script)';

  let os = '';
  if (/Windows NT 10/i.test(ua)) os = 'Windows 10/11';
  else if (/Windows/i.test(ua)) os = 'Windows';
  else if (/iPhone|iPad|iPod/i.test(ua)) os = 'iOS';
  else if (/Mac OS X|Macintosh/i.test(ua)) os = 'macOS';
  else if (/Android/i.test(ua)) os = 'Android';
  else if (/Linux/i.test(ua)) os = 'Linux';
  return os ? `${browser} sur ${os}` : browser;
};

const METRICS = [
  { key: 'all', label: 'Évènements (total)', color: 'text-foreground', bar: 'bg-slate-400', testid: 'audit-total-events' },
  { key: 'success', label: 'Connexions réussies', color: 'text-emerald-600', bar: 'bg-emerald-500', testid: 'audit-success' },
  { key: 'failed', label: 'Connexions échouées', color: 'text-red-600', bar: 'bg-red-500', testid: 'audit-failed' },
  { key: 'ips', label: 'Adresses IP uniques', color: 'text-blue-600', bar: 'bg-blue-500', testid: 'audit-unique-ips' },
  { key: 'users', label: 'Utilisateurs uniques', color: 'text-violet-600', bar: 'bg-violet-500', testid: 'audit-unique-users' },
];

// Petit graphique en barres sans dépendance externe
const BarChart = ({ data, color, testid }) => {
  const max = Math.max(1, ...data.map((d) => d.value));
  if (!data.length) return <div className="text-sm text-muted-foreground py-6">Aucune donnée à afficher pour cette catégorie.</div>;
  return (
    <div className="flex items-end gap-2 h-48 overflow-x-auto pb-2" data-testid={testid}>
      {data.map((d, i) => (
        <div key={i} className="flex flex-col items-center justify-end gap-1 min-w-[42px] flex-1">
          <span className="text-xs font-semibold">{d.value}</span>
          <div
            className={`w-full rounded-t ${color} transition-all`}
            style={{ height: `${(d.value / max) * 100}%`, minHeight: d.value > 0 ? '4px' : '0px' }}
            title={`${d.label}: ${d.value}`}
          />
          <span className="text-[10px] text-muted-foreground truncate max-w-[60px]" title={d.label}>{d.label}</span>
        </div>
      ))}
    </div>
  );
};

const AuditLogs = () => {
  const { user, token } = useAuth();
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [activeSessions, setActiveSessions] = useState(0);
  const [metric, setMetric] = useState('all');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (user?.user_type === 'fondateur') load(); /* eslint-disable-next-line */ }, [user]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [res, sess] = await Promise.all([
        fetchLoginAudit({ limit: 500, email: email || undefined }, token),
        fetchLoginSessions({ limit: 200, email: email || undefined }, token),
      ]);
      setEvents(res.events || []);
      setSummary(res.summary || null);
      setDaily(res.daily || []);
      setSessions(sess.sessions || []);
      setActiveSessions(sess.active_sessions || 0);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Erreur de chargement');
      setEvents([]); setSummary(null); setDaily([]); setSessions([]);
    } finally { setLoading(false); }
  };

  const exportCSV = () => {
    const csv = toCSV(filteredEvents);
    const date = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    download(`padaid-login-events-${date}.csv`, csv);
  };

  // Évènements filtrés selon le compteur cliqué
  const filteredEvents = useMemo(() => {
    if (metric === 'success') return events.filter((e) => e.event === 'login' && e.success);
    if (metric === 'failed') return events.filter((e) => e.event === 'login' && !e.success);
    return events;
  }, [events, metric]);

  // Données du graphique selon le compteur sélectionné
  const chart = useMemo(() => {
    if (metric === 'ips' || metric === 'users') {
      const field = metric === 'ips' ? 'ip' : 'email';
      const counts = {};
      events.forEach((e) => { const k = e[field] || '—'; counts[k] = (counts[k] || 0) + 1; });
      const arr = Object.entries(counts).map(([label, value]) => ({ label, value }));
      arr.sort((a, b) => b.value - a.value);
      return { data: arr.slice(0, 12) };
    }
    const valueKey = metric === 'success' ? 'success' : metric === 'failed' ? 'failed' : 'total';
    return {
      data: daily.map((d) => ({
        label: new Date(d.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
        value: d[valueKey],
      })),
    };
  }, [metric, daily, events]);

  const activeMeta = METRICS.find((m) => m.key === metric) || METRICS[0];

  if (!user || user.user_type !== 'fondateur') return <div className="p-6 text-red-500">Accès réservé au fondateur</div>;

  return (
    <div className="p-6 space-y-6" data-testid="audit-logs-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-semibold">Connexions & Inscriptions</h1>
        <div className="flex gap-2">
          <button className="px-3 py-1 rounded bg-muted hover:bg-muted/80" onClick={load} disabled={loading} data-testid="audit-refresh-btn">Rafraîchir</button>
          <button className="px-3 py-1 rounded bg-primary text-primary-foreground" onClick={exportCSV} disabled={!filteredEvents.length} data-testid="audit-export-btn">Exporter CSV</button>
        </div>
      </div>
      {error && <div className="text-sm text-red-500" data-testid="audit-error">{error}</div>}

      {/* Compteurs cliquables */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="audit-summary">
          {METRICS.map((m) => {
            const value = {
              all: summary.total_events, success: summary.successful_logins,
              failed: summary.failed_logins, ips: summary.unique_ips, users: summary.unique_users,
            }[m.key] ?? 0;
            const selected = metric === m.key;
            return (
              <button
                key={m.key}
                onClick={() => setMetric(m.key)}
                data-testid={`${m.testid}-btn`}
                className={`text-left rounded-lg border p-3 bg-card transition-all hover:shadow-md hover:-translate-y-0.5 ${selected ? 'ring-2 ring-primary border-primary' : ''}`}
              >
                <div className={`text-2xl font-bold ${m.color}`} data-testid={m.testid}>{value}</div>
                <div className="text-xs text-muted-foreground">{m.label}</div>
                <div className="text-[10px] text-primary mt-1">{selected ? '● Affiché' : 'Cliquer pour voir'}</div>
              </button>
            );
          })}
        </div>
      )}

      {/* Graphique de la catégorie sélectionnée */}
      <div className="rounded-lg border p-4 bg-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold">
            {metric === 'ips' ? 'Répartition par adresse IP (top 12)'
              : metric === 'users' ? 'Répartition par utilisateur (top 12)'
                : `${activeMeta.label} — par jour`}
          </h2>
          {metric !== 'all' && (
            <button className="text-xs text-muted-foreground underline" onClick={() => setMetric('all')} data-testid="audit-reset-filter">Réinitialiser</button>
          )}
        </div>
        <BarChart data={chart.data} color={activeMeta.bar} testid="audit-barchart" />
      </div>

      {/* Sessions & durée active */}
      <div className="rounded-lg border p-4 bg-card space-y-3" data-testid="audit-sessions">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">Sessions & durée de connexion</h2>
          <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-600" data-testid="audit-active-sessions">
            {activeSessions} session(s) active(s)
          </span>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-muted">
                <th className="p-2 text-left">Utilisateur</th>
                <th className="p-2 text-left">Connexion</th>
                <th className="p-2 text-left">Fin / dernière activité</th>
                <th className="p-2 text-left">Durée active</th>
                <th className="p-2 text-left">IP</th>
                <th className="p-2 text-left">Navigateur</th>
                <th className="p-2 text-left">Statut</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.id} className="border-t" data-testid="audit-session-row">
                  <td className="p-2">{s.email}</td>
                  <td className="p-2">{s.login_at ? new Date(s.login_at).toLocaleString('fr-FR') : '—'}</td>
                  <td className="p-2">{(s.logout_at || s.last_seen_at) ? new Date(s.logout_at || s.last_seen_at).toLocaleString('fr-FR') : '—'}</td>
                  <td className="p-2 font-semibold">{formatDuration(s.duration_seconds)}</td>
                  <td className="p-2">{s.ip || '—'}</td>
                  <td className="p-2" title={s.user_agent}>{parseUserAgent(s.user_agent)}</td>
                  <td className="p-2">
                    {s.active
                      ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600">En ligne</span>
                      : <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">Terminée</span>}
                  </td>
                </tr>
              ))}
              {sessions.length === 0 && (
                <tr><td className="p-3 text-muted-foreground" colSpan="7">Aucune session enregistrée</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recherche + tableau détaillé */}
      <div className="flex gap-2">
        <input className="border rounded px-2 py-1 flex-1" placeholder="Filtrer par email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="audit-email-filter" />
        <button className="px-3 py-1 rounded bg-primary text-primary-foreground" onClick={load} disabled={loading}>Rechercher</button>
      </div>

      {loading ? <div>Chargement…</div> : (
        <div className="overflow-auto border rounded">
          <div className="px-3 py-2 text-xs text-muted-foreground bg-muted/50">
            {metric === 'all' ? 'Tous les évènements' : `Filtré : ${activeMeta.label}`} — {filteredEvents.length} ligne(s)
          </div>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-muted">
                <th className="p-2 text-left">Email</th>
                <th className="p-2 text-left">Type</th>
                <th className="p-2 text-left">Succès</th>
                <th className="p-2 text-left">IP</th>
                <th className="p-2 text-left">Navigateur</th>
                <th className="p-2 text-left">Date</th>
                <th className="p-2 text-left">Social</th>
              </tr>
            </thead>
            <tbody>
              {filteredEvents.map((ev) => (
                <tr key={ev.id} className="border-t">
                  <td className="p-2">{ev.email}</td>
                  <td className="p-2">{ev.event || (ev.social ? 'login_social' : 'login')}</td>
                  <td className="p-2">{ev.success ? '✅' : '❌'}</td>
                  <td className="p-2">{ev.ip || '—'}</td>
                  <td className="p-2" title={ev.user_agent}>{parseUserAgent(ev.user_agent)}</td>
                  <td className="p-2">{new Date(ev.created_at).toLocaleString('fr-FR')}</td>
                  <td className="p-2">{ev.social ? 'Oui' : 'Non'}</td>
                </tr>
              ))}
              {filteredEvents.length === 0 && (
                <tr><td className="p-3 text-muted-foreground" colSpan="7">Aucun évènement</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
